import { Controller } from "@hotwired/stimulus"
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode"

// Scans a barcode via camera or manual entry, then looks up the product.
// Uses consensus-based scanning: requires the same code to be read multiple
// times before accepting it, which filters out misreads.
export default class extends Controller {
  static targets = ["scanRegion", "startBtn", "stopBtn", "manualInput", "scanStatus"]

  // Number of consecutive matching reads required before accepting
  static REQUIRED_READS = 1

  connect() {
    this.scanner = null
    this.scanning = false
    this.resetConsensus()
  }

  disconnect() {
    this.stopScanner()
  }

  resetConsensus() {
    this.lastCode = null
    this.readCount = 0
  }

  async startScanner() {
    this.startBtnTarget.style.display = "none"
    this.resetConsensus()

    this.scanner = new Html5Qrcode("barcode-scan-region")

    try {
      await this.scanner.start(
        {
          facingMode: { ideal: "environment" },
          width: { min: 640, ideal: 1920, max: 1920 },
          height: { min: 480, ideal: 1080, max: 1080 },
          focusMode: "continuous"
        },
        {
          fps: 10,
          formatsToSupport: [
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
          ]
        },
        (decodedText) => {
          this.handleDetection(decodedText)
        },
        () => {
          // No code found in this frame - ignore
        }
      )

      this.scanning = true
      this.stopBtnTarget.style.display = "inline-block"
      this.showStatus("Scanning... Point camera at a barcode")
    } catch (err) {
      this.showStatus("Camera access denied or unavailable. Use manual entry.")
      this.startBtnTarget.style.display = "inline-block"
    }
  }

  handleDetection(code) {
    if (!this.scanning) return

    if (code === this.lastCode) {
      this.readCount++
    } else {
      this.lastCode = code
      this.readCount = 1
    }

    if (this.readCount >= this.constructor.REQUIRED_READS) {
      this.stopScanner()
      this.lookupBarcode(code)
    }
  }

  async stopScanner() {
    if (this.scanning && this.scanner) {
      try {
        await this.scanner.stop()
      } catch (e) {
        // Ignore stop errors
      }
      this.scanning = false
    }
    this.stopBtnTarget.style.display = "none"
    this.startBtnTarget.style.display = "inline-block"
  }

  manualLookup() {
    const code = this.manualInputTarget.value.trim()
    if (code) {
      this.lookupBarcode(code)
    }
  }

  async lookupBarcode(code) {
    this.showStatus(`Looking up ${code}...`)

    try {
      const response = await fetch(`/barcodes/lookup?code=${encodeURIComponent(code)}`, {
        headers: { "Accept": "text/vnd.turbo-stream.html" }
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
        // Stream actions don't fire turbo:frame-load, so dispatch it manually
        // to trigger the modal controller to open the Bootstrap modal.
        document.getElementById("modal")?.dispatchEvent(new Event("turbo:frame-load"))
      } else {
        this.showStatus("Error looking up barcode. Please try again.")
      }
    } catch (error) {
      this.showStatus("Error looking up barcode. Please try again.")
    }
  }

  showStatus(message) {
    this.scanStatusTarget.textContent = message
  }
}
